import { useMemo, useRef } from 'react';
import { graphql, navigate } from 'gatsby';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import useInternalLinks from '@/hooks/useInternalLinks';
import countryList from '@/components/Forms/constants/countryList';
import {
  StyledCampaignForm,
  StyledFormHeadline,
  StyledFormFooter,
  StyledButton,
  StyledForm,
} from './CampaignForm.styles';
import { zodResolver } from '@hookform/resolvers/zod';
import Input from '../Forms/components/Input/Input';
import Select from '../Forms/components/Select/Select';
import FormError from '../Forms/components/FormError/FormError';
import errorLogger from '@/utils/errorLogger';
import pushInDataLayer from '@/utils/pushInDataLayer';
import { type ReCAPTCHA } from 'react-google-recaptcha';
import GoogleRecaptcha from '../Forms/components/GoogleRecaptcha/GoogleRecaptcha';
import { isTokenRecaptchaValid } from '@/utils/validateGoogleRecaptchaToken';
import LegalText, { type LegalTextProps } from '../Forms/components/LegalText/LegalText';
import getCheqHiddenFieldValue from '../Forms/components/CheqHiddenField/getCheqHiddenFieldValue';
import CheqHiddenField from '../Forms/components/CheqHiddenField/CheqHiddenField';
import MarketingOptOut from '../Forms/components/MarketingOptOut/MarketingOptOut';
import { campaignFormSchema } from './validation';
import { useLocation } from '@reach/router';
import { type BlockHTMLId } from '@/utils/renderBlock';
import useSetGeolocatedCountry from '../Forms/hooks/useSetGeolocatedCountry';
import useFormComplete from '../Forms/hooks/useFormComplete';
import getSalesForceId from '@/utils/getSalesForceId';

const CampaignForm = ({
  campaignFormElements: elements,
  campaignFormSystem: { codename },
}: Queries.CAMPAIGN_FORMFragment & BlockHTMLId) => {
  const {
    headline,
    submit_button_title,
    campaign_id,
    legal_text,
    success_redirect_page,
    options,
    drop_shadow,
    type,
  } = elements;
  const { t } = useTranslation();
  const getInternalLinkURLByCodename = useInternalLinks();
  const recaptchaRef = useRef<ReCAPTCHA>(null);
  const hideLabel = options?.value?.includes('hide_labels') ?? false;
  const hasDropShadow = drop_shadow?.value?.[0] === 'show' && drop_shadow.value.length > 0;
  const { search, pathname } = useLocation();
  const searchParams = useMemo(() => new URLSearchParams(search), [search]);
  const leadCampaignId = getSalesForceId();

  const {
    register,
    control,
    handleSubmit,
    formState: { isSubmitting, errors },
    setError,
    setValue,
  } = useForm<typeof campaignFormSchema>({
    mode: 'onBlur',
    resolver: zodResolver(campaignFormSchema),
    defaultValues: {
      campaign_id: leadCampaignId ?? campaign_id.value,
      form_type: 'campaign',
      company: '',
      country: null,
      email: '',
      first_name: '',
      job_title: '',
      last_name: '',
      referral_code: '',
      campaign_type: type?.value ?? 'gated_asset',
      utm_source: searchParams.get('utm_source'),
      utm_medium: searchParams.get('utm_medium'),
      gads_ad_group: searchParams.get('gads_ad_group'),
      gads_campaign: searchParams.get('gads_campaign'),
      gads_keyword: searchParams.get('gads_keyword'),
    },
  });

  useSetGeolocatedCountry({ setValue, field: 'country', countryList });
  const isFormCompleteReady = useFormComplete({
    setValue,
    selectMappings: {
      country: countryList,
    },
  });
  const enhanceFormComplete = options?.value?.includes('enhance_form_complete') ?? false;
  const showReferralCode = options?.value?.includes('show_referral_code') ?? false;
  const shouldHideFields = enhanceFormComplete && !isFormCompleteReady;

  const redirectURL =
    getInternalLinkURLByCodename(success_redirect_page.value.system.codename) ?? '/';

  const onSubmit = handleSubmit(async (formValues) => {
    const token = await recaptchaRef.current?.executeAsync();
    await isTokenRecaptchaValid(token ?? null, 'campain-form');

    try {
      const cq_req_id = getCheqHiddenFieldValue();

      const requestBody = {
        ...formValues,
        cq_req_id, // Cheq Request ID
      };

      const response = await fetch('/api/submit-form', {
        method: 'POST',
        body: JSON.stringify(requestBody),
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error(response.statusText);
      }

      pushInDataLayer({
        event: 'submittedForm',
        submittedFormData: {
          type: 'campaign',
          email: requestBody.email,
          country: requestBody.country,
          fromPage: pathname,
          campaignId: leadCampaignId ?? requestBody.campaign_id,
          ...(success_redirect_page.value.elements.automatic_download_asset_url?.value && {
            assetName: new URL(
              success_redirect_page.value.elements.automatic_download_asset_url.value
            ).pathname
              .split('/')
              .pop(),
          }),
        },
      });

      await new Promise((resolve) => setTimeout(resolve, 1000));

      navigate(redirectURL);
    } catch (error) {
      errorLogger(error);
      setError('root', { type: 'custom', message: t('form.Could not submit form') });
    }
  });

  const emailInput = (
    <Input
      type="email"
      id="email"
      required
      placeholder={t('form.Email')}
      formGroupProps={{ label: t('form.Email'), hideLabel }}
      {...register('email')}
      errors={errors}
    />
  );

  return (
    <StyledCampaignForm hasDropShadow={hasDropShadow} data-codename={codename} id={codename}>
      <StyledFormHeadline as="h3">{headline.value}</StyledFormHeadline>
      <StyledForm noValidate onSubmit={onSubmit} id={codename}>
        {enhanceFormComplete && emailInput}
        <Input
          type="text"
          id="first_name"
          required
          placeholder={t('form.First Name')}
          formGroupProps={{ label: t('form.First Name'), hideLabel, isHidden: shouldHideFields }}
          {...register('first_name')}
          errors={errors}
        />
        <Input
          type="text"
          id="last_name"
          required
          placeholder={t('form.Last Name')}
          formGroupProps={{ label: t('form.Last Name'), hideLabel, isHidden: shouldHideFields }}
          {...register('last_name')}
          errors={errors}
        />
        <Input
          type="text"
          id="company"
          required
          placeholder={t('form.Company Name')}
          formGroupProps={{ label: t('form.Company Name'), hideLabel, isHidden: shouldHideFields }}
          {...register('company')}
          errors={errors}
        />
        {!enhanceFormComplete && emailInput}
        <Input
          type="text"
          id="job_title"
          placeholder={t('form.Job Title')}
          formGroupProps={{ label: t('form.Job Title'), hideLabel, isHidden: shouldHideFields }}
          {...register('job_title')}
          errors={errors}
        />
        <CheqHiddenField />
        <Controller
          control={control}
          name="country"
          render={({ field }) => (
            <Select
              errors={errors}
              required
              id="country"
              placeholder={t('form.Select a country')}
              options={countryList}
              formGroupProps={{ label: 'Country', hideLabel }}
              {...field}
            />
          )}
        />
        <Input
          type="text"
          id="referral_code"
          placeholder={t('form.Referral Code')}
          formGroupProps={{
            label: t('form.Referral Code'),
            hideLabel,
            isHidden: shouldHideFields || !showReferralCode,
          }}
          {...register('referral_code')}
          errors={errors}
        />
        <MarketingOptOut
          label={t('form.Marketing Emails Opt Out')}
          errors={errors}
          register={register}
        />
        <input type="hidden" {...register('form_type')} />
        <input type="hidden" {...register('campaign_id')} />
        <input type="hidden" {...register('campaign_type')} />
        <StyledFormFooter>
          <GoogleRecaptcha ref={recaptchaRef} />
          <StyledButton type="submit" disabled={isSubmitting} variant="primary">
            {isSubmitting ? t('form.Loading') : submit_button_title.value}
          </StyledButton>
          <LegalText richText={legal_text as LegalTextProps['richText']} />
          {errors?.root?.message && <FormError>{errors.root.message}</FormError>}
        </StyledFormFooter>
      </StyledForm>
    </StyledCampaignForm>
  );
};

export const fragment = graphql`
  fragment CAMPAIGN_FORM on kontent_item_campaign_form {
    campaignFormElements: elements {
      headline {
        value
      }
      submit_button_title {
        value
      }
      campaign_id {
        value
      }
      legal_text {
        value
        links {
          link_id
          codename
        }
      }
      success_redirect_page {
        value {
          system {
            codename
          }
          elements {
            automatic_download_asset_url {
              value
            }
          }
        }
      }
      options {
        value
      }
      drop_shadow {
        value
      }
      type {
        value
      }
    }
    campaignFormSystem: system {
      id
      codename
    }
  }
`;

export default CampaignForm;
